import { useLingui } from '@lingui/react/macro'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { indicatorPalette, type IndicatorKind } from './indicator-colors'

const DOT_SIZE = { sm: 'size-1.5', md: 'size-2' } as const
const DASH_PILL = 'bg-muted-foreground/30 inline-block h-0.5 w-1.5 rounded-full'

export interface CompletionDotsItem {
  key: string
  kind: IndicatorKind
  /** Whether the underlying test/survey was completed (drives the colored vs muted dot). */
  completed: boolean
  /** When true, the dim is on the auto-generated standard (not custom-described); rendered slightly faded. */
  standard?: boolean | null
  /**
   * The integrant has this indicator defined, but there's nothing to compare it against so no score
   * is produced (e.g. the position dimension sits on the standard profile). Paints a faded coloured
   * background with a dash — distinguishing "has the indicator, no comparison" from "never tested".
   */
  defined?: boolean | null
  /** Optional numeric score. When present (and completed), shown in the tooltip rows; the per-row score column is omitted entirely if no item has a score. */
  score?: number | null
  label: string
}

export interface CompletionDotsProps {
  items: CompletionDotsItem[]
  className?: string
  size?: 'sm' | 'md'
  tooltipSide?: 'top' | 'bottom'
  /** Render dots inline with their labels (no tooltip). Only applies to the `dots` variant. */
  expanded?: boolean
  /** Label used in the aria description for non-completed/no-score items. */
  pendingLabel?: string
  /**
   * Trigger style:
   * - `dots` (default): a row of colour-coded status dots.
   * - `scores`: a row of per-indicator score badges (the value, tinted by indicator colour).
   * Both share the same tooltip listing every item.
   */
  variant?: 'dots' | 'scores'
  /** For the `scores` variant: key of the selected item — that badge gets the filled + bordered hint. */
  activeKey?: string
  /**
   * Force every score into the empty "no comparison" state (muted dashed badge), regardless of the
   * underlying value. Used when the displayed scores don't apply to the current context — e.g. the
   * team member list's fit percentages are precomputed for "member vs the whole self-assessed team",
   * so they're greyed out when the active comparison uses a different evaluation source. Completion
   * dots stay coloured (they reflect test completion, which is still accurate).
   */
  muted?: boolean
}

/**
 * One score as a small rounded badge: tinted bg + coloured text from the shared palette. The
 * selected hint is the fill + border — `selected` gets a denser tint of its own colour
 * (`bg-current/20` vs the lighter `activeBg`) plus a coloured border; the rest are borderless.
 * `standard` fades it a touch. Three states:
 * - `hasScore`: a real comparison score → coloured badge with the number.
 * - `defined` (no score): the integrant has this indicator, but there's nothing to compare it
 *   against → faded coloured background with a coloured dash, so the data still reads as present.
 * - neither → muted dashed-outline dash.
 */
function ScoreBadge(props: {
  kind: IndicatorKind
  score: number | null | undefined
  hasScore: boolean
  defined?: boolean | null
  selected?: boolean
  standard?: boolean | null
  className?: string
}) {
  const palette = indicatorPalette[props.kind]
  return (
    <span
      className={cn(
        'inline-flex h-4.25 min-w-[1.25rem] items-center justify-center rounded-full border border-transparent px-1 text-[11px] leading-none tabular-nums transition-colors',
        props.hasScore
          ? cn(
              palette.activeText,
              props.selected ? cn('bg-current/10', palette.activeBorderStrong) : palette.activeBg,
              props.standard && 'opacity-70',
            )
          : props.defined
            ? cn(palette.activeText, palette.activeBg, 'opacity-70')
            : 'border-dashed border-muted-foreground/30 text-muted-foreground/50',
        props.className,
      )}
    >
      {props.hasScore ? (
        props.score
      ) : (
        <span
          className={cn(
            'inline-block h-0.5 w-1.5 rounded-full',
            props.defined ? 'bg-current/60' : 'bg-muted-foreground/30',
          )}
          aria-hidden
        />
      )}
    </span>
  )
}

/**
 * Inline `[icon] [score]` used by the `dots` variant to surface the active dim's and the global
 * score next to the status dots (no own tooltip — the cluster shares one). Dash when no score.
 */
function ScoreInline(props: { kind: IndicatorKind; score: number | null | undefined; hasScore: boolean }) {
  const Icon = indicatorPalette[props.kind].icon
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1',
        props.hasScore ? indicatorPalette[props.kind].activeText : 'text-muted-foreground/50',
      )}
    >
      <Icon className="size-3.5 shrink-0" />
      <span className="inline-flex min-w-[2ch] items-center text-xs leading-none font-semibold tabular-nums">
        {props.hasScore ? props.score : <span className={cn(DASH_PILL, 'mx-0.5')} aria-hidden />}
      </span>
    </span>
  )
}

export function CompletionProgressHeader(props: {
  progressValue: string | number
  progressRatio: number
  progressStrokeClass?: string
  title: string
  subtitle: string
}) {
  const progressStrokeClass = props.progressStrokeClass ?? 'stroke-muted-foreground'

  return (
    <div className="border-border/50 relative border-b px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="relative size-10 shrink-0">
          <svg className="size-10 -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" fill="none" className="stroke-muted" strokeWidth="3" />
            <circle
              cx="18"
              cy="18"
              r="14"
              fill="none"
              className={cn(progressStrokeClass, 'transition-[stroke-dasharray] duration-500')}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${props.progressRatio * 88} 88`}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">
            {props.progressValue}
          </span>
        </div>
        <div>
          <p className="text-sm font-semibold">{props.title}</p>
          <p className="text-muted-foreground text-xs">{props.subtitle}</p>
        </div>
      </div>
    </div>
  )
}

/**
 * Per-dim completion cluster + tooltip listing each item's status.
 *
 * Trigger (`variant`):
 * - `dots` (default): a row of small colored dots (completed) / muted dots (pending), plus —
 *   when scores are present — the active dim's and the global summary's `[icon] [score]` inline.
 * - `scores`: a row of per-indicator score badges (value tinted by indicator colour, the
 *   `activeKey` badge highlighted).
 * Both drive `ScoreRow`.
 * Tooltip (shared by both): one row per item with `[icon] [score-or-dash] [label]`. The score
 * column is omitted entirely when no item provides a score, so test/survey usages stay compact
 * while score-bearing usages (match scores) render a full numeric column.
 *
 * Canonical completion component used everywhere in the app — keep this single implementation
 * in sync and avoid forking it.
 */
export function CompletionDots(props: CompletionDotsProps) {
  const { t } = useLingui()
  const items = props.items
  const size = props.size ?? 'sm'
  const expanded = props.expanded ?? false
  const className = props.className
  const tooltipSide = props.tooltipSide ?? 'top'
  const variant = props.variant ?? 'dots'
  const activeKey = props.activeKey
  const muted = props.muted ?? false
  const pendingLabel = props.pendingLabel ?? t`Pending`

  if (items.length === 0)
    return <span className={cn('inline-flex items-center', size === 'sm' ? 'size-3.5' : 'size-4', className)} />

  if (expanded) {
    return (
      <span className={cn('flex items-center gap-1.5 overflow-hidden', className)}>
        {items.map((item) => (
          <span key={item.key} className="flex min-w-0 shrink items-center gap-1">
            <span
              aria-label={item.label}
              className={cn(
                'inline-block shrink-0 rounded-full',
                DOT_SIZE[size],
                item.completed ? indicatorPalette[item.kind].barFill : 'bg-muted-foreground/40',
                item.completed && item.standard && 'opacity-70',
              )}
            />
            <span
              className={cn(
                'min-w-0 truncate text-[10px] font-medium',
                item.completed ? 'text-foreground/70' : 'text-muted-foreground/40 italic',
              )}
            >
              {item.label}
            </span>
          </span>
        ))}
      </span>
    )
  }

  const anyHasScore = !muted && items.some((i) => i.score != null)

  // For the `dots` variant: the active dim's pill (the sorted, non-global item) and the global
  // summary pill are surfaced inline next to the dots — the rest of the dims live in the dots.
  const globalItem = items.find((it) => it.kind === 'global')
  const activeDimItem = activeKey != null ? items.find((it) => it.key === activeKey && it.kind !== 'global') : undefined

  const ariaLabel = items
    .map((it) => `${it.label}: ${it.completed && it.score != null ? it.score : pendingLabel}`)
    .join(', ')

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {variant === 'scores' ? (
          <span className={cn('inline-flex w-fit cursor-default items-center gap-1', className)} aria-label={ariaLabel}>
            {items.map((it) => {
              const hasScore = !muted && it.completed && it.score != null
              return (
                <ScoreBadge
                  key={it.key}
                  kind={it.kind}
                  score={it.score}
                  hasScore={hasScore}
                  defined={!hasScore && !!it.defined}
                  selected={activeKey != null && it.key === activeKey}
                  standard={it.standard}
                  className={it.kind === 'global' ? 'ml-0.5' : undefined}
                />
              )
            })}
          </span>
        ) : (
          <span className={cn('inline-flex cursor-default items-center gap-1.5', className)} aria-label={ariaLabel}>
            <span className="inline-flex items-center gap-0.5 py-0.5">
              {items
                .filter((it) => it.kind !== 'global')
                .map((it) => (
                  <span
                    key={it.key}
                    className={cn(
                      'shrink-0 rounded-full',
                      DOT_SIZE[size],
                      it.completed ? indicatorPalette[it.kind].barFill : 'bg-muted-foreground/30',
                      it.completed && it.standard && 'opacity-70',
                    )}
                  />
                ))}
            </span>
            {activeDimItem && (
              <ScoreInline
                kind={activeDimItem.kind}
                score={activeDimItem.score}
                hasScore={!muted && activeDimItem.completed && activeDimItem.score != null}
              />
            )}
            {globalItem && (
              <ScoreInline
                kind={globalItem.kind}
                score={globalItem.score}
                hasScore={!muted && globalItem.completed && globalItem.score != null}
              />
            )}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent side={tooltipSide} className="grid gap-1.5 px-2.5 py-2">
        {items.map((it) => {
          const Icon = indicatorPalette[it.kind].icon
          const hasScore = !muted && it.completed && it.score != null
          // The integrant has this indicator if the test was completed, or it's defined-without-comparison.
          // In the `scores` variant the row IS a score, so "present" must track the score's presence
          // (like the global summary already does) — otherwise a gated/absent score (e.g. a co-worker
          // whose results the viewer can't see) would still paint a coloured icon + black label while
          // its value shows a dash. Mirror the trigger's ScoreBadge: coloured only when there's a real
          // score or a defined-without-comparison dim.
          const present = variant === 'scores' ? hasScore || !!it.defined : it.completed || !!it.defined
          return (
            <div key={it.key} className="grid grid-cols-[auto_1fr] items-center gap-1.5 text-xs">
              <span className="inline-flex items-center gap-1">
                <Icon
                  className={cn(
                    'size-4 shrink-0',
                    present ? indicatorPalette[it.kind].activeText : 'text-muted-foreground/40',
                    present && (it.standard || !hasScore) && 'opacity-70',
                  )}
                  strokeWidth={1.5}
                />
                {anyHasScore && (
                  <span
                    className={cn(
                      'inline-flex min-w-[2ch] items-center text-xs font-semibold tabular-nums',
                      present && indicatorPalette[it.kind].activeText,
                      present && (it.standard || !hasScore) && 'opacity-70',
                    )}
                  >
                    {hasScore ? it.score : <span className={cn(DASH_PILL, 'mx-0.5')} aria-hidden />}
                  </span>
                )}
              </span>
              <span className={cn('font-medium', present ? 'text-foreground' : 'text-muted-foreground')}>
                {it.label}
              </span>
            </div>
          )
        })}
      </TooltipContent>
    </Tooltip>
  )
}
